import React, { Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { wrapPromise } from "go-core";
import { LoadingContainer } from "go-core/components/Loading";
import { FILTER_VALUE_SEPARATOR } from "go-list/core/components/Filter/services";
import ReportData from "go-report/reports";
import {
	getChartReport,
	getChartType,
	getLastMonthFilterBetween,
	getOrganizationIds,
	getOrganizationIdsFromReportFetchRequest,
	getReport,
	getReportCustomFieldsResources,
	parseOrganizationIds,
} from "go-report/reports/services/report-service";
import { getSelectedSegmentForReportConfig } from "go-report/reports/services/segment-service";
import { ReportConfig } from "go-report/reports/services/types";
import { selectUser } from "go-security/services/users/selectors";
import { UserState } from "go-security/services/users/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { apiReport } from "../../../../../../../../services/Api/Report/apiReport";
import {
	getCommonPaymentStatusFilter,
	getCommonPaymentStatusGroup,
	getCommonPaymentStatusGroupInfo,
} from "../../../../../../../../utils/reports";
import ReportOrdersUtils from "../../../../../../../../utils/reports/ReportOrdersUtils";
import { ReportSaleUtils } from "../../../../../../../../utils/reports/ReportSaleUtils";
import ReportShareUtils from "../../../../../../../../utils/reports/ReportShareUtils";

interface Props {
	resource?: Record<string, any>;
	setOrganizationsIds: (organizationIds: number[]) => void;
	user: UserState;
}

const listName = "ORDER_REPORT_GROUP";
const resourceType = "ORDER_REPORT_GROUP";

const Report = ({ resource, setOrganizationsIds, user }: Props) => {
	const { t } = useTranslation();
	const [filtersParams, setFilterParams] = useState("");
	const segmentContext = useContext(SegmentContext);

	if (!resource) return null;
	const data = resource.read();

	let config = {
		reportConfigId: "orders",
		type: "order",
		defaultGroup: "NONE",
		aggregatePrefix: "sales",
		selectedColumns: ReportOrdersUtils.getSelectedColumns("orders"),
		chart: {
			title: t("modules.report_bill.header.report.title"),
			lineTitle: t("modules.report_bill.field.line_title.title"),
			aggregate: "total_money",
			type: getChartType(),
			parseGroup: (group: string) => {
				if (group === "HOUR_OF_DAY") return "CLOSED_AT_HOUR_OF_DAY";
				if (group === "DAY_OF_MONTH") return "REPORT_DATE_DAY_OF_MONTH";
				if (group === "DATE") return "REPORT_DATE_DATE";
				if (group === "DAY_OF_WEEK") return "REPORT_DATE_DAY_OF_WEEK";
				if (group === "MONTH") return "REPORT_DATE_MONTH";
				return group;
			},
		},
		groups: [
			...ReportShareUtils.getGroupReportCommonGroups(t),
			...ReportOrdersUtils.getGroups(t),
			...ReportShareUtils.getGroups(t),
			getCommonPaymentStatusGroup(t),
			{ id: "CANCELLATION_REASON", name: t("modules.order.field.cancellation_reason.title") },
		],
		groupsInfo: {
			...ReportShareUtils.getGroupsInfo(t),
			...getCommonPaymentStatusGroupInfo(t),
			CANCELLATION_REASON: (groupByValue: Record<string, any>) => {
				if (groupByValue.name === "empty") return "-";
				return t(`enums.orders.cancellation_reason.${groupByValue.name}`);
			},
		},
		showPercent: true,
		columns: ReportOrdersUtils.getColumns(t),
		externalSelectedFilters: [
			{
				id: "date_range",
				filterType: "date",
				value: getLastMonthFilterBetween().split("=")[1],
				condition: getLastMonthFilterBetween().split("=")[0],
				visible: true,
			},
			{
				id: "organization_ids",
				filterType: "list",
				condition: "e",
				value: getOrganizationIds(user.organizations?.map((x) => x.id).join(FILTER_VALUE_SEPARATOR)),
				visible: true,
			},
		],
		filters: [
			...ReportOrdersUtils.getFilters(t, filtersParams),
			...ReportShareUtils.getFilters(t, user),
			getCommonPaymentStatusFilter(t),
			ReportSaleUtils.getFilters(t).find((filter) => filter.id === "fiscalization_status"),
		],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
				columns: ReportOrdersUtils.getSelectedColumns("orders"),
				filters: [
					{
						filterId: "date_range",
						filterType: "date",
						value: getLastMonthFilterBetween().split("=")[1],
						condition: getLastMonthFilterBetween().split("=")[0],
					},
					{
						filterId: "organization_ids",
						filterType: "list",
						condition: "e",
						value: getOrganizationIds(user.organizations?.map((x) => x.id).join(FILTER_VALUE_SEPARATOR)),
					},
				],
			},
		],
		fetch: (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			setOrganizationsIds(getOrganizationIdsFromReportFetchRequest(params.f));
			setFilterParams(params.f);
			return Promise.all([
				getReport(
					apiReport.getReportsCustom,
					params,
					getOrganizationIdsFromReportFetchRequest(params.f),
					config.filters,
					config.type,
					sourceToken
				),
				getChartReport(
					apiReport.getReportsCustom,
					params,
					"NONE",
					getOrganizationIdsFromReportFetchRequest(params.f),
					config.filters,
					config.type,
					sourceToken
				),
			]);
		},
		fetchChartData: (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			return getChartReport(
				apiReport.getReportsCustom,
				params,
				"NONE",
				getOrganizationIdsFromReportFetchRequest(params.f),
				config.filters,
				config.type,
				sourceToken
			);
		},
		saveSegment: (segment: SegmentType) => {
			return segmentContext.save(listName, resourceType, segment);
		},
		exportConfig: {
			title: t("modules.report_bill.field.export_config.title"),
			filename: t("modules.report_bill.field.export_config_filename.title"),
			pdfOrientation: "ASK",
		},
		withCompareDateRange: true,
		selectedSegment: getSelectedSegmentForReportConfig(data.segments, "all"),
	} as ReportConfig;

	data.fields = getReportCustomFieldsResources(data.fields, ["ORDER"], t);

	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		filterValues: data.filter_values,
	};

	return <ReportData config={config} />;
};

const UserReportsBillsPage = (): JSX.Element => {
	const user = useSelector(selectUser);
	const [organizationsIds, setOrganizationsIds] = useState<number[]>(() =>
		parseOrganizationIds(user.organizations ?? [])
	);
	const organizationIdsAsString = organizationsIds.join();
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContext = useContext(SegmentContext);
	const { t } = useTranslation();

	const customFieldResourceTypes = [
		{
			type: "ORDER",
			name: t(`enums.custom_fields.resources.ORDER`),
		},
	];

	useEffect(() => {
		const params: Record<string, any> = {};
		if (organizationsIds?.length > 0) {
			params.organization_id = organizationIdsAsString;
		}

		setResource(wrapPromise(segmentContext.get(listName, resourceType, customFieldResourceTypes, params)));
	}, [organizationIdsAsString]);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<Report resource={resource} setOrganizationsIds={setOrganizationsIds} user={user} />
		</Suspense>
	);
};

export default UserReportsBillsPage;
